import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@cieda/djayma-ui";
import { Download, ChevronDown } from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ExportColumn {
  readonly key: string;
  readonly label: string;
}

export interface ExportButtonProps {
  readonly data: readonly Record<string, unknown>[];
  readonly columns: readonly ExportColumn[];
  readonly filename: string;
  readonly formats?: readonly ("csv" | "excel" | "pdf")[];
}

// ---------------------------------------------------------------------------
// Export logic: CSV generation
// ---------------------------------------------------------------------------

function toCsv(
  data: readonly Record<string, unknown>[],
  columns: readonly ExportColumn[],
): string {
  const header = columns.map((c) => `"${c.label}"`).join(",");
  const rows = data.map((row) =>
    columns
      .map((c) => {
        const val = row[c.key];
        if (val == null) return '""';
        return `"${String(val).replace(/"/g, '""')}"`;
      })
      .join(","),
  );
  // BOM prefix for Excel compatibility with French characters
  return `\uFEFF${header}\n${rows.join("\n")}`;
}

function downloadBlob(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ExportButton({
  data,
  columns,
  filename,
  formats = ["csv"],
}: ExportButtonProps) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  const handleOutsideClick = useCallback((e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open, handleOutsideClick]);

  const handleExport = (format: string) => {
    if (format === "csv") {
      const csv = toCsv(data, columns);
      downloadBlob(csv, `${filename}.csv`, "text/csv;charset=utf-8;");
    }
    // Excel and PDF would require additional libraries (e.g. xlsx, jsPDF)
    // Placeholder for future implementation
    setOpen(false);
  };

  const FORMAT_LABELS: Record<string, string> = {
    csv: "CSV",
    excel: "Excel",
    pdf: "PDF",
  };

  // Single format: simple button
  if (formats.length <= 1) {
    return (
      <Button
        variant="outline"
        onClick={() => handleExport(formats[0] ?? "csv")}
        className="gap-2"
      >
        <Download className="h-4 w-4" />
        Exporter
      </Button>
    );
  }

  // Multiple formats: dropdown
  return (
    <div ref={dropdownRef} className="relative inline-block">
      <Button
        variant="outline"
        onClick={() => setOpen((prev) => !prev)}
        className="gap-2"
      >
        <Download className="h-4 w-4" />
        Exporter
        <ChevronDown className="h-3.5 w-3.5" />
      </Button>

      {open && (
        <div className="absolute right-0 z-10 mt-1 min-w-[140px] rounded-md border border-stone-200 bg-white py-1 shadow-lg">
          {formats.map((fmt) => (
            <button
              key={fmt}
              onClick={() => handleExport(fmt)}
              className="flex w-full items-center px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
            >
              {FORMAT_LABELS[fmt] ?? fmt.toUpperCase()}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
